import React, { useEffect, useState } from 'react';
import { isRecord, type AdminApiClient, type JsonRecord } from './api-client';

const labels: Record<string, string> = {
  visualStyle: '页面风格',
  navigationTitle: '顶部导航标题',
  titleInImage: '头图已包含标题',
  topicsSubtitle: '城市专题副标题',
  topics: '城市特色专题',
  keywords: '城市特色关键词',
  title: '页面标题',
  body: '说明内容',
  text: '说明内容',
  imageUrl: '图片地址',
  images: '轮播图片',
  autoplay: '自动播放',
  interval: '切换间隔（毫秒）',
  columns: '服务项目',
  linkText: '链接文字',
};
const actions: Record<string, string> = {
  OPEN_PRODUCT: '打开线路详情',
  OPEN_ORDER: '查看订单',
  CREATE_PENDING_ORDER: '填写预订订单',
  START_PAYMENT: '发起支付',
  REQUEST_REFUND: '申请退款',
  OPEN_CUSTOMER_SERVICE: '联系在线客服',
  OPEN_ENTERPRISE_WECHAT_GUIDE: '添加服务向导',
  CLAIM_COUPON: '领取优惠券',
  SWITCH_CITY: '切换城市',
  OPEN_AGREEMENT: '查看预订须知',
};
const sources: Record<string, string> = {
  'product.title': '当前产品名称',
  'product.coverMedia': '当前产品封面',
  'city.name': '当前城市名称',
  'product.summary': '当前产品简介',
  'page.agreement': '当前页面预订须知',
  'app.customerService': '小程序客服配置',
  'product.skus': '当前产品套餐',
  'product.tags': '当前产品标签',
  'product.routeStops': '当前产品行程',
};
const sourceSlots: Record<string, string[]> = {
  'product.title': ['title'],
  'city.name': ['title'],
  'product.coverMedia': ['imageUrl', 'image', 'coverMedia'],
  'product.summary': ['body', 'text'],
  'page.agreement': ['body', 'text'],
  'app.customerService': ['customerService'],
  'product.skus': ['columns', 'skus'],
  'product.tags': ['columns', 'tags'],
  'product.routeStops': ['columns', 'routeStops'],
};
const icons: Record<string, string> = {
  ticket: '门票',
  car: '车辆',
  guide: '向导',
  headset: '客服',
  price: '价格',
};
type Props = {
  api: AdminApiClient;
  appId: string;
  pageId: string;
  expectedDraftVersion: number;
  onDone: () => void;
};
type Draft = {
  props: JsonRecord;
  bindings: JsonRecord;
  actionIds: string[];
  status: string;
  instanceKey: string;
  sortOrder: string;
};
function record(value: unknown): JsonRecord {
  return isRecord(value) ? value : {};
}
function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}
function valueText(value: unknown) {
  return value === null || value === undefined ? '' : String(value);
}
function readObject(value: string, label: string): JsonRecord {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new Error(`${label}格式不正确，请检查后保存。`);
  }
  if (!isRecord(parsed)) throw new Error(`${label}需要是对象格式。`);
  return parsed;
}
function initialDraft(component?: JsonRecord): Draft {
  return {
    props: record(component?.props),
    bindings: record(component?.dataBindings),
    actionIds: strings(component?.actionIds),
    status: component?.status === 'DISABLED' ? 'DISABLED' : 'ACTIVE',
    instanceKey: valueText(component?.instanceKey),
    sortOrder: valueText(component?.sortOrder ?? 0),
  };
}
function schemaFor(definition: JsonRecord, component?: JsonRecord): JsonRecord {
  if (isRecord(definition.propsSchema)) return definition.propsSchema;
  // Older drafts can omit their schema. Only expose fields already present; never invent a field contract.
  return Object.fromEntries(
    Object.entries(record(component?.props)).map(([key, value]) => [
      key,
      { type: Array.isArray(value) ? 'array' : typeof value },
    ]),
  );
}

export function CmsComponentForm(props: Props & { definitions: JsonRecord[] }) {
  const [definitionId, setDefinitionId] = useState('');
  const selected = props.definitions.find((definition) => definition.id === definitionId);
  return (
    <div className="cms-component-create">
      <label className="cms-component-type">
        组件类型
        <select value={definitionId} onChange={(event) => setDefinitionId(event.target.value)}>
          <option value="">请选择组件</option>
          {props.definitions.map((definition) => (
            <option key={String(definition.id)} value={String(definition.id)}>
              {valueText(definition.displayName)}
            </option>
          ))}
        </select>
      </label>
      {selected && <CmsFieldsForm key={definitionId} {...props} definition={selected} />}
    </div>
  );
}

export function CmsComponentUpdateForm(
  props: Props & { component: JsonRecord; definitions?: JsonRecord[] },
) {
  const nested = record(props.component.componentDefinition);
  const definition = Object.keys(nested).length
    ? nested
    : (props.definitions?.find(
        (item) =>
          item.id === props.component.componentDefinitionId ||
          item.componentKey === props.component.componentKey,
      ) ?? props.component);
  return <CmsFieldsForm {...props} definition={definition} />;
}

function CmsFieldsForm({
  api,
  appId,
  pageId,
  definition,
  component,
  expectedDraftVersion,
  onDone,
}: Props & { definition: JsonRecord; component?: JsonRecord }) {
  const [draft, setDraft] = useState<Draft>(() => initialDraft(component));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [advancedProps, setAdvancedProps] = useState('');
  const [advancedBindings, setAdvancedBindings] = useState('');
  useEffect(() => {
    setDraft(initialDraft(component));
    setAdvanced(false);
    setError('');
    setResult('');
  }, [component]);
  const schema = schemaFor(definition, component);
  const fields = Object.entries(schema).filter(([, rule]) => isRecord(rule));
  const allowedActions = strings(definition.allowedActionIds ?? component?.allowedActionIds);
  const allowedBindings = strings(definition.allowedDataBindings);
  const bindingFields = Array.from(
    new Set([...fields.map(([key]) => key), ...Object.keys(draft.bindings)]),
  )
    .map((key) => ({
      key,
      choices: allowedBindings.filter((source) => sourceSlots[source]?.includes(key)),
    }))
    .filter(({ key, choices }) => choices.length > 0 || key in draft.bindings);
  const endpoint = `/api/admin/m2/apps/${encodeURIComponent(appId)}/cms/pages/${encodeURIComponent(pageId)}/components`;
  const setProp = (key: string, value: unknown) =>
    setDraft((current) => {
      const next = { ...current.props };
      if (value === undefined) delete next[key];
      else next[key] = value;
      return { ...current, props: next };
    });
  const advancedFields = fields.filter(
    ([key, rule]) =>
      ['object', 'array'].includes(String(record(rule).type)) &&
      !['images', 'columns', 'topics', 'keywords'].includes(key),
  );
  const unknownCount = Object.keys(draft.props).filter((key) => !(key in schema)).length;
  const updateEntry = (key: string, index: number, next: JsonRecord) => {
    const values = Array.isArray(draft.props[key]) ? [...(draft.props[key] as unknown[])] : [];
    values[index] = next;
    setProp(key, values);
  };
  function validate(next: Draft) {
    for (const [key, rawRule] of fields) {
      const rule = record(rawRule),
        value = next.props[key];
      if (
        rule.required === true &&
        (value === undefined || value === null || (typeof value === 'string' && !value.trim()))
      )
        throw new Error(`请填写${labels[key] ?? key}。`);
      if (value === undefined || value === null) continue;
      const type = rule.type;
      if (
        type === 'array'
          ? !Array.isArray(value)
          : type === 'object'
            ? !isRecord(value)
            : type === 'asset'
              ? typeof value !== 'string' || !/^(asset|media):\/\/[A-Za-z0-9._/-]+$/.test(value)
              : typeof value !== type
      )
        throw new Error(`${labels[key] ?? key}格式与当前组件要求不一致。`);
      if (type === 'number' && !Number.isFinite(value))
        throw new Error(`请填写有效的${labels[key] ?? key}。`);
    }
    if (Object.values(next.bindings).some((value) => typeof value !== 'string'))
      throw new Error('内容来源配置需要使用有效的来源名称。');
    if (!component && (!next.sortOrder.trim() || !Number.isInteger(Number(next.sortOrder))))
      throw new Error('排序须为整数。');
    return next;
  }
  async function save() {
    if (busy) return;
    setError('');
    setResult('');
    try {
      const next = validate(
        advanced
          ? {
              ...draft,
              props: readObject(advancedProps, '展示配置'),
              bindings: readObject(advancedBindings, '内容来源'),
            }
          : draft,
      );
      setBusy(true);
      if (component) {
        await api.send(`${endpoint}/${encodeURIComponent(String(component.id))}`, 'PATCH', {
          props: next.props,
          dataBindings: next.bindings,
          actionIds: next.actionIds,
          status: next.status,
          expectedDraftVersion,
        });
      } else {
        const instanceKey =
          next.instanceKey.trim() ||
          `${valueText(definition.componentKey)}-${crypto.randomUUID().slice(0, 8)}`;
        await api.send(endpoint, 'POST', {
          componentDefinitionId: definition.id,
          instanceKey,
          sortOrder: Number(next.sortOrder),
          props: next.props,
          dataBindings: next.bindings,
          actionIds: next.actionIds,
          expectedDraftVersion,
        });
      }
      setDraft(next);
      setAdvanced(false);
      setResult(component ? '组件已保存' : '组件已添加');
      onDone();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : '保存失败，请稍后重试。');
    } finally {
      setBusy(false);
    }
  }
  function openAdvanced() {
    setAdvancedProps(JSON.stringify(draft.props, null, 2));
    setAdvancedBindings(JSON.stringify(draft.bindings, null, 2));
    setAdvanced(true);
    setError('');
  }
  function applyAdvanced() {
    try {
      const props = readObject(advancedProps, '展示配置'),
        bindings = readObject(advancedBindings, '内容来源');
      setDraft((current) => ({ ...current, props, bindings }));
      setAdvanced(false);
      setError('');
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : '配置格式不正确。');
    }
  }
  return (
    <form
      className="typed-form wide cms-schema-form"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <fieldset className="cms-schema-fields" disabled={busy || advanced}>
        <legend>展示内容</legend>
        {fields.map(([key, rawRule]) => {
          const rule = record(rawRule),
            value = draft.props[key],
            label = labels[key] ?? valueText(rule.label ?? rule.title ?? key);
          if (key === 'visualStyle')
            return (
              <label key={key}>
                页面风格
                <select
                  value={valueText(value) || 'banna-illustrated'}
                  onChange={(e) => setProp(key, e.target.value)}
                >
                  <option value="banna-illustrated">统一城市插画风格</option>
                  <option value="coastal-editorial">海风人文风格（潮汕）</option>
                </select>
              </label>
            );
          if (key === 'keywords')
            return (
              <label key={key}>
                城市特色关键词
                <input
                  value={strings(value).join('、')}
                  placeholder="用顿号分隔，最多四项"
                  onChange={(e) =>
                    setProp(
                      key,
                      e.target.value
                        .split(/[、，,]/)
                        .map((s) => s.trim())
                        .filter(Boolean)
                        .slice(0, 4),
                    )
                  }
                />
              </label>
            );
          if (key === 'topics') {
            const entries = Array.isArray(value) ? value.map(record) : [];
            return (
              <div key={key} className="cms-repeat-field">
                <div className="cms-repeat-heading">
                  <strong>城市特色专题</strong>
                  <button
                    type="button"
                    disabled={entries.length >= 8}
                    onClick={() =>
                      setProp(key, [
                        ...entries,
                        { label: '', description: '', iconKey: 'route', keywords: '' },
                      ])
                    }
                  >
                    添加专题
                  </button>
                </div>
                <small>
                  不配置时按当前城市产品自动生成；关键词匹配线路名称或标签，无匹配产品的专题隐藏。
                </small>
                {entries.map((item, index) => (
                  <div className="cms-repeat-row" key={index}>
                    <label>
                      标题
                      <input
                        maxLength={12}
                        value={valueText(item.label)}
                        onChange={(e) =>
                          updateEntry(key, index, { ...item, label: e.target.value })
                        }
                      />
                    </label>
                    <label>
                      说明
                      <input
                        maxLength={24}
                        value={valueText(item.description)}
                        onChange={(e) =>
                          updateEntry(key, index, { ...item, description: e.target.value })
                        }
                      />
                    </label>
                    <label>
                      图标
                      <select
                        value={valueText(item.iconKey)}
                        onChange={(e) =>
                          updateEntry(key, index, { ...item, iconKey: e.target.value })
                        }
                      >
                        {Object.entries({
                          forest: '自然',
                          elephant: '野象',
                          culture: '文化',
                          night: '夜游',
                          family: '亲子',
                          car: '包车',
                          ticket: '票务',
                          route: '线路',
                          lighthouse: '南澳灯塔',
                          bridge: '潮州古桥',
                          island: '海岛海湾',
                          arcade: '汕头骑楼',
                          stay: '住宿慢游',
                          heritage: '揭阳城门',
                        }).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      匹配关键词
                      <input
                        value={valueText(item.keywords)}
                        placeholder="例如：雨林、植物园"
                        onChange={(e) =>
                          updateEntry(key, index, { ...item, keywords: e.target.value })
                        }
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        setProp(
                          key,
                          entries.filter((_, i) => i !== index),
                        )
                      }
                    >
                      移除
                    </button>
                  </div>
                ))}
              </div>
            );
          }
          if (rule.type === 'array' && (key === 'images' || key === 'columns')) {
            const list = Array.isArray(value) ? value : [];
            const isImages = key === 'images';
            return (
              <div className="cms-repeat-field" key={key}>
                <div className="cms-repeat-heading">
                  <strong>{label}</strong>
                  <button
                    type="button"
                    onClick={() =>
                      setProp(key, [
                        ...list,
                        isImages ? { url: '', altText: '' } : { iconKey: 'ticket', label: '' },
                      ])
                    }
                  >
                    添加{isImages ? '图片' : '服务项'}
                  </button>
                </div>
                {list.map((entry, index) => {
                  const item = isRecord(entry)
                    ? entry
                    : isImages && typeof entry === 'string'
                      ? { url: entry }
                      : null;
                  if (!item)
                    return <p key={index}>第 {index + 1} 项使用特殊格式，请在高级设置维护。</p>;
                  return (
                    <div className="cms-repeat-row" key={index}>
                      <span>{index + 1}</span>
                      {isImages ? (
                        <>
                          <label>
                            图片地址
                            <input
                              aria-label={`图片 ${index + 1} 地址`}
                              type="url"
                              value={valueText(item.url)}
                              onChange={(event) =>
                                updateEntry(key, index, { ...item, url: event.target.value })
                              }
                              placeholder="https://"
                            />
                          </label>
                          <label>
                            图片说明
                            <input
                              aria-label={`图片 ${index + 1} 说明`}
                              value={valueText(item.altText)}
                              onChange={(event) =>
                                updateEntry(key, index, { ...item, altText: event.target.value })
                              }
                            />
                          </label>
                        </>
                      ) : (
                        <>
                          <label>
                            图标
                            <select
                              aria-label={`服务项 ${index + 1} 图标`}
                              value={valueText(item.iconKey)}
                              onChange={(event) =>
                                updateEntry(key, index, { ...item, iconKey: event.target.value })
                              }
                            >
                              {!icons[valueText(item.iconKey)] && (
                                <option value={valueText(item.iconKey)}>
                                  {item.iconKey ? '原有图标' : '不显示图标'}
                                </option>
                              )}
                              {Object.entries(icons).map(([icon, text]) => (
                                <option value={icon} key={icon}>
                                  {text}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            服务文字
                            <input
                              aria-label={`服务项 ${index + 1} 文字`}
                              value={valueText(item.label ?? item.name)}
                              onChange={(event) =>
                                updateEntry(key, index, { ...item, label: event.target.value })
                              }
                            />
                          </label>
                        </>
                      )}
                      <button
                        type="button"
                        aria-label={`移除${isImages ? '图片' : '服务项'} ${index + 1}`}
                        onClick={() =>
                          setProp(
                            key,
                            list.filter((_, target) => target !== index),
                          )
                        }
                      >
                        移除
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          }
          if (['array', 'object'].includes(String(rule.type))) return null;
          if (key === 'interval' && schema.autoplay && draft.props.autoplay === false) return null;
          return (
            <label
              key={key}
              className={
                rule.type === 'boolean'
                  ? 'cms-schema-toggle'
                  : key === 'body'
                    ? 'cms-schema-wide'
                    : ''
              }
            >
              {draft.bindings[key] ? `${label}（备用）` : label}
              {rule.type === 'boolean' ? (
                <select
                  value={value === undefined ? '' : value === true ? 'yes' : 'no'}
                  onChange={(event) =>
                    setProp(
                      key,
                      event.target.value === '' ? undefined : event.target.value === 'yes',
                    )
                  }
                >
                  <option value="">按组件默认</option>
                  <option value="yes">开启</option>
                  <option value="no">关闭</option>
                </select>
              ) : key === 'body' || key === 'text' ? (
                <textarea
                  required={rule.required === true}
                  value={valueText(value)}
                  onChange={(event) => setProp(key, event.target.value)}
                />
              ) : (
                <input
                  required={rule.required === true}
                  type={rule.type === 'number' ? 'number' : key === 'imageUrl' ? 'url' : 'text'}
                  step={rule.type === 'number' ? 'any' : undefined}
                  value={valueText(value)}
                  onChange={(event) =>
                    setProp(
                      key,
                      rule.type === 'number'
                        ? event.target.value === ''
                          ? undefined
                          : Number(event.target.value)
                        : event.target.value,
                    )
                  }
                />
              )}
              {Boolean(draft.bindings[key]) && (
                <small className="cms-binding-hint">
                  优先显示{sources[valueText(draft.bindings[key])] ?? '所选来源'}
                  ；来源未提供时使用填写内容。
                </small>
              )}
            </label>
          );
        })}
        {advancedFields.length > 0 && (
          <p className="cms-schema-wide">
            {advancedFields.map(([key]) => labels[key] ?? key).join('、')}
            使用扩展格式，可在高级设置中维护。
          </p>
        )}
        {!fields.length && <p>该组件未提供展示字段定义；已有配置会保留。</p>}
      </fieldset>
      <div className="cms-schema-behavior">
        {bindingFields.length > 0 && (
          <fieldset className="cms-schema-fields" disabled={busy || advanced}>
            <legend>内容来源</legend>
            {bindingFields.map(({ key, choices }) => {
              const current = valueText(draft.bindings[key]);
              return (
                <label key={key}>
                  {labels[key] ?? key}
                  <select
                    aria-label={`${labels[key] ?? key}内容来源`}
                    value={current}
                    onChange={(event) =>
                      setDraft((previous) => {
                        const next = { ...previous.bindings };
                        if (event.target.value) next[key] = event.target.value;
                        else delete next[key];
                        return { ...previous, bindings: next };
                      })
                    }
                  >
                    <option value="">使用填写内容</option>
                    {current && !choices.includes(current) && (
                      <option value={current}>保留原有来源</option>
                    )}
                    {choices.map((source) => (
                      <option key={source} value={source}>
                        {sources[source] ?? source}
                      </option>
                    ))}
                  </select>
                </label>
              );
            })}
          </fieldset>
        )}
        <fieldset className="cms-schema-actions" disabled={busy}>
          <legend>受控动作</legend>
          {allowedActions.map((action) => (
            <label key={action}>
              <input
                type="checkbox"
                checked={draft.actionIds.includes(action)}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    actionIds: event.target.checked
                      ? [...current.actionIds, action]
                      : current.actionIds.filter((id) => id !== action),
                  }))
                }
              />
              {actions[action] ?? action}
            </label>
          ))}
          {!allowedActions.length && <span>该组件没有可选点击操作。</span>}
          {draft.actionIds.some((action) => !allowedActions.includes(action)) && (
            <span>已有扩展动作会继续保留。</span>
          )}
        </fieldset>
      </div>
      <div className="cms-schema-options">
        {component ? (
          <label>
            展示状态
            <select
              value={draft.status}
              disabled={busy}
              onChange={(event) =>
                setDraft((current) => ({ ...current, status: event.target.value }))
              }
            >
              <option value="ACTIVE">启用</option>
              <option value="DISABLED">停用</option>
            </select>
          </label>
        ) : (
          <label>
            显示排序
            <input
              type="number"
              step="1"
              value={draft.sortOrder}
              disabled={busy}
              onChange={(event) =>
                setDraft((current) => ({ ...current, sortOrder: event.target.value }))
              }
            />
          </label>
        )}
        <button type="button" disabled={busy || advanced} onClick={openAdvanced}>
          高级设置{unknownCount ? `（保留 ${unknownCount} 项扩展）` : ''}
        </button>
      </div>
      {advanced && (
        <fieldset className="cms-schema-advanced" disabled={busy}>
          <legend>高级设置</legend>
          {!component && (
            <label>
              组件标识（可选）
              <input
                value={draft.instanceKey}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, instanceKey: event.target.value }))
                }
                placeholder="留空自动生成"
              />
            </label>
          )}
          <label>
            展示配置
            <textarea
              value={advancedProps}
              onChange={(event) => setAdvancedProps(event.target.value)}
            />
          </label>
          <label>
            内容来源配置
            <textarea
              value={advancedBindings}
              onChange={(event) => setAdvancedBindings(event.target.value)}
            />
          </label>
          <div className="button-row">
            <button type="button" onClick={applyAdvanced}>
              应用高级设置
            </button>
            <button
              type="button"
              onClick={() => {
                setAdvanced(false);
                setError('');
              }}
            >
              取消高级编辑
            </button>
          </div>
        </fieldset>
      )}
      <div className="button-row cms-schema-wide">
        <button className="primary" disabled={busy}>
          {busy ? '保存中…' : component ? '保存组件' : '添加组件'}
        </button>
        {component && (
          <button
            type="button"
            className="danger"
            disabled={busy}
            onClick={async () => {
              if (!window.confirm('确认删除这个草稿组件？已发布版本不会被直接改写。')) return;
              setBusy(true);
              setError('');
              try {
                await api.send(
                  `${endpoint}/${encodeURIComponent(String(component.id))}`,
                  'DELETE',
                  { expectedDraftVersion },
                );
                onDone();
              } catch (failure) {
                setError(failure instanceof Error ? failure.message : '删除失败，请稍后重试。');
              } finally {
                setBusy(false);
              }
            }}
          >
            删除组件
          </button>
        )}
        {result && <span role="status">{result}</span>}
      </div>
      {error && (
        <p className="cms-schema-wide" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
